import React, { useState, useEffect, useRef } from 'react';
import { notify } from '../services/notify';
import { 
  Search, 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  Loader2, 
  Tag, 
  Briefcase,
  ChevronRight,
  CreditCard,
  Banknote,
  CheckCircle2,
  Package,
  FileText,
  Layers,
  X,
  Barcode,
  User,
  UserPlus,
  Edit,
  Pause,
  ArrowRight,
  RotateCcw,
  Building2,
  ChevronDown,
  Printer,
  History as HistoryIcon
} from 'lucide-react';
import { 
  db, 
  auth, 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  increment,
  limit,
  arrayUnion,
  handleFirestoreError,
  OperationType
} from '../firebase';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { logHistory } from '../services/historyService';
import { postInvoiceJournal, postPaymentJournal, postInventoryJournal, postCreditNoteJournal, createAutoJournal, getAccountById, ensureAccountByCode } from '../services/accountingService';
import { reduceStockFIFO } from '../services/inventoryService';
import HistoryNotesView from './HistoryNotesView';

const getLocalAuthToken = () => typeof window !== 'undefined' ? localStorage.getItem('app_auth_token') : null;
const isLocalSession = () => Boolean(getLocalAuthToken());
const localApiHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getLocalAuthToken() || ''}` });

import { SearchableSelect } from './ui/SearchableSelect';
import { getDefaultCurrency } from '../lib/currency';
import { localizePrintableHtml } from '../lib/localization';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface RecipeItem {
  itemId: string;
  name: string;
  quantity: number;
  unit: string;
}

interface Recipe {
  id: string;
  name: string;
  isActive: boolean;
  components: RecipeItem[];
}

interface Item {
  id: string;
  code: string;
  name: string;
  category?: string;
  description: string;
  unitPrice: number;
  quantity?: number;
  allowNegativeStock?: boolean;
  type: 'product' | 'service';
  status: 'active' | 'archived';
  enablePOS?: boolean;
  hasExpiry?: boolean;
  hideFromSellScreen?: boolean;
  taxRate?: string;
  barcode?: string;
  imageUrl?: string;
  storeStock?: { [storeId: string]: number };
  stockRecords?: any[];
  recipeManagement?: boolean;
  recipes?: Recipe[];
  trackLotNumber?: boolean;
  cost?: number;
  costPrice?: number;
}

interface CartItem extends Item {
  quantity: number;
  discount?: number;
  discountType?: 'amount' | 'percent';
  notes?: string;
  taxAmount?: number;
  account?: string;
  lotNumber?: string;
  expiryDate?: string;
  warehouseId?: string;
  warehouseName?: string;
}

export default function POSView({ 
  orgId, 
  selectedStoreId,
  setSelectedStoreId,
  resumedSaleId,
  isReturn,
  onClear,
  language = 'en'
}: { 
  orgId: string, 
  selectedStoreId: string,
  setSelectedStoreId: (id: string) => void,
  resumedSaleId?: string | null,
  isReturn?: boolean,
  onClear?: () => void,
  language?: string
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [resumedInvoiceId, setResumedInvoiceId] = useState<string | null>(null);
  const [resumedInvoiceNumber, setResumedInvoiceNumber] = useState<string | null>(null);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [editingItem, setEditingItem] = useState<CartItem | null>(null);

  const [selectedItemForAdd, setSelectedItemForAdd] = useState<Item | null>(null);
  const [addItemForm, setAddItemForm] = useState({
    quantity: 1,
    discount: 0,
    discountType: 'percent' as 'amount' | 'percent',
    warehouseId: '',
    warehouseName: '',
    lotNumber: '',
    expiryDate: '',
    unitPrice: 0
  });

  // Custom Item States
  const [customItemName, setCustomItemName] = useState('');
  const [customCostPrice, setCustomCostPrice] = useState('');
  const [customSalePrice, setCustomSalePrice] = useState('');
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);

  // Barcode States
  const [barcodeInput, setBarcodeInput] = useState('');
  const [showBarcodeInput, setShowBarcodeInput] = useState(false);

  // Customer States
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [orgSettings, setOrgSettings] = useState<any>(null);

  // Order Summary States
  const [orderNotes, setOrderNotes] = useState('');
  const [orderDiscount, setOrderDiscount] = useState(0);
  const [orderDiscountType, setOrderDiscountType] = useState<'amount' | 'percent'>('percent');

  // New POS States
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [chartOfAccounts, setChartOfAccounts] = useState<any[]>([]);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>('');
  const [amountPaid, setAmountPaid] = useState<number>(0);
  const [alreadyPaid, setAlreadyPaid] = useState<number>(0);

  // Parked Invoices States
  const [showParkedModal, setShowParkedModal] = useState(false);
  const [parkedInvoices, setParkedInvoices] = useState<any[]>([]);
  const [lotNumbers, setLotNumbers] = useState<any[]>([]);
  const [saleLotNumber, setSaleLotNumber] = useState('');
  const [saleExpiryDate, setSaleExpiryDate] = useState('');

  // Store Selection States
  const [stores, setStores] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [trackingCategories, setTrackingCategories] = useState<any[]>([]);

  const validateDiscount = (value: number, type: 'amount' | 'percent') => {
    if (type !== 'percent' || value === 0) return true;
    
    const selectedStore = stores.find(s => s.id === selectedStoreId);
    if (!selectedStore?.discountRules || selectedStore.discountRules.length === 0) return true;
    
    const currentUser = auth.currentUser;
    if (!currentUser) return true;
    
    const userRule = selectedStore.discountRules.find((rule: any) => 
      rule.userIds.includes(currentUser.uid)
    );
    
    if (!userRule) return true;
    
    if (value < userRule.minDiscount || value > userRule.maxDiscount) {
      return false;
    }
    
    return true;
  };

  const handleOrderDiscountChange = (val: number) => {
    if (val !== 0 && !validateDiscount(val, orderDiscountType)) {
      const selectedStore = stores.find(s => s.id === selectedStoreId);
      const userRule = selectedStore?.discountRules?.find((rule: any) => 
        rule.userIds.includes(auth.currentUser?.uid)
      );
      if (userRule) {
        notify.error(`Discount must be between ${userRule.minDiscount}% and ${userRule.maxDiscount}%`);
      } else {
        notify.error('You do not have permission to apply discounts.');
      }
      return;
    }
    setOrderDiscount(val);
  };

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      Promise.all([
        fetch(`/api/organizations/${orgId}/stores`, { headers: localApiHeaders() }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/tracking-categories`, { headers: localApiHeaders() }).then(r => r.json()),
      ]).then(([storesData, cats]) => {
        if (cancelled) return;
        const activeStores = Array.isArray(storesData) ? storesData.filter((s: any) => s.status === 'active') : [];
        setStores(activeStores);
        setWarehouses([]);
        setTrackingCategories(Array.isArray(cats) ? cats : []);
        if (activeStores.length > 0 && !selectedStoreId) setShowStoreSelector(true);
      }).catch(err => { if (!cancelled) console.error('POS stores load failed:', err); });
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'stores'),
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const storesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStores(storesData);
      
      // Show store selector if not selected
      if (storesData.length > 0 && !selectedStoreId) {
        setShowStoreSelector(true);
      }
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'stores'));

    const warehousesQuery = query(collection(db, 'warehouses'), where('orgId', '==', orgId));
    const unsubscribeWarehouses = onSnapshot(warehousesQuery, (snapshot) => {
      setWarehouses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'warehouses'));

    const trackingQ = query(collection(db, 'tracking_categories'), where('orgId', '==', orgId));
    const unsubscribeTracking = onSnapshot(trackingQ, (snapshot) => {
      setTrackingCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'tracking_categories'));

    return () => {
      unsubscribe();
      unsubscribeWarehouses();
      unsubscribeTracking();
    };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      Promise.all([
        fetch(`/api/organizations/${orgId}/bank-accounts`, { headers: localApiHeaders() }).then(r => r.json()),
        fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers: localApiHeaders() }).then(r => r.json()),
      ]).then(([banks, coa]) => {
        if (cancelled) return;
        setBankAccounts(Array.isArray(banks) ? banks : []);
        setChartOfAccounts(Array.isArray(coa) ? coa : []);
      }).catch(err => { if (!cancelled) console.error('POS bank/coa load failed:', err); });
      return () => { cancelled = true; };
    }

    // Fetch bank accounts
    const bankQuery = query(
      collection(db, 'bank_accounts'), 
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );
    const unsubscribeBank = onSnapshot(bankQuery, (snapshot) => {
      const accounts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBankAccounts(accounts);
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'bank_accounts'));

    // Fetch chart of accounts
    const coaQuery = query(
      collection(db, 'chart_of_accounts'),
      where('orgId', '==', orgId),
      orderBy('code', 'asc')
    );
    const unsubscribeCOA = onSnapshot(coaQuery, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setChartOfAccounts(docs);
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts'));

    return () => {
      unsubscribeBank();
      unsubscribeCOA();
    };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      Promise.all([
        fetch(`/api/organizations/${orgId}/documents?types=receipt`, { headers: localApiHeaders() })
          .then(r => r.json())
          .then((docs: any[]) => (Array.isArray(docs) ? docs.filter((d: any) => ['Awaiting Payment','Parked'].includes(d.status)) : [])),
        fetch(`/api/organizations/${orgId}/lots`, { headers: localApiHeaders() }).then(r => r.json()),
      ]).then(([parked, lots]) => {
        if (cancelled) return;
        setParkedInvoices(parked);
        setLotNumbers(Array.isArray(lots) ? lots : []);
      }).catch(err => { if (!cancelled) console.error('POS parked/lots load failed:', err); });
      return () => { cancelled = true; };
    }

    // Fetch parked invoices (POS invoices with Parked or Awaiting Payment status)
    const q = query(
      collection(db, 'documents'),
      where('orgId', '==', orgId),
      where('source', '==', 'POS'),
      where('status', 'in', ['Awaiting Payment', 'Parked']),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setParkedInvoices(docs);
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'documents'));

    // Fetch Lot Numbers
    const lotQ = query(
      collection(db, 'lot_numbers'),
      where('orgId', '==', orgId)
    );
    const unsubLot = onSnapshot(lotQ, (snapshot) => {
      setLotNumbers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'lot_numbers'));

    return () => {
      unsubscribe();
      unsubLot();
    };
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      Promise.all([
        fetch(`/api/organizations/${orgId}/settings`, { headers: localApiHeaders() }).then(r => r.ok ? r.json() : null),
        fetch(`/api/organizations/${orgId}/items`, { headers: localApiHeaders() }).then(r => r.json()),
      ]).then(([settings, itemsData]) => {
        if (cancelled) return;
        if (settings) setOrgSettings(settings);
        const posItems = Array.isArray(itemsData)
          ? itemsData.filter((item: any) => item.enablePOS && item.status === 'active') as Item[]
          : [];
        setItems(posItems);
        setLoading(false);
      }).catch(err => {
        if (cancelled) return;
        console.error('POS items load failed:', err);
        setLoading(false);
      });
      return () => { cancelled = true; };
    }

    // Fetch organization settings
    const fetchOrgSettings = async () => {
      try {
        const docRef = doc(db, 'settings', orgId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setOrgSettings(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching org settings:', error);
      }
    };

    fetchOrgSettings();

    const q = query(
      collection(db, 'items'),
      where('orgId', '==', orgId),
      where('status', '==', 'active'),
      where('enablePOS', '==', true),
      orderBy('name', 'asc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Item));
      setItems(docs);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'items');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      fetch(`/api/organizations/${orgId}/contacts`, { headers: localApiHeaders() })
        .then(r => r.json())
        .then(data => {
          if (cancelled) return;
          const customers = Array.isArray(data)
            ? data.filter((c: any) => c.status?.toLowerCase() === 'active' && ['customer','both'].includes(c.type?.toLowerCase()))
                  .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
            : [];
          setContacts(customers);
        })
        .catch(err => { if (!cancelled) console.error('POS contacts load failed:', err); });
      return () => { cancelled = true; };
    }

    const q = query(
      collection(db, 'contacts'), 
      where('orgId', '==', orgId)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter((c: any) => 
          (c.status?.toLowerCase() === 'active') && 
          (c.type?.toLowerCase() === 'customer' || c.type?.toLowerCase() === 'both')
        )
        .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
      setContacts(docs);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'contacts');
    });

    return () => unsubscribe();
  }, [orgId]);

  const hasResumed = useRef<string | null>(null);

  useEffect(() => {
    if (!resumedSaleId) {
      hasResumed.current = null;
      return;
    }

    if (hasResumed.current !== resumedSaleId && !loading) {
      const fetchSale = async () => {
        try {
          let saleData: any = null;
          if (isLocalSession()) {
            const localToken = getLocalAuthToken();
            const r = await fetch(`/api/organizations/${orgId}/documents/${resumedSaleId}`, {
              headers: { 'Authorization': `Bearer ${localToken || ''}` }
            });
            if (r.ok) saleData = await r.json();
          } else {
            const saleDoc = await getDoc(doc(db, 'documents', resumedSaleId));
            if (saleDoc.exists()) saleData = { id: saleDoc.id, ...saleDoc.data() };
          }
          if (saleData) {
            if (isReturn) {
              prepareReturn(saleData);
            } else {
              resumeParkedInvoice(saleData);
            }
            hasResumed.current = resumedSaleId;
          }
        } catch (error) {
          console.error("Error fetching resumed sale:", error);
        }
      };
      fetchSale();
    }
  }, [resumedSaleId, isReturn, loading]);

  const filteredItems = items.filter(item => {
    if (item.hideFromSellScreen) return false;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         item.code.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const addToCart = (item: Item) => {
    // Determine default warehouse 
    const defaultWarehouseId = warehouses.length > 0 ? warehouses[0].id : '';
    const defaultWarehouseName = warehouses.find(s => s.id === defaultWarehouseId)?.name || '';

    setAddItemForm({
      quantity: 1,
      discount: 0,
      discountType: 'percent',
      warehouseId: defaultWarehouseId,
      warehouseName: defaultWarehouseName,
      lotNumber: '',
      expiryDate: '',
      unitPrice: item.unitPrice || 0
    });
    setSelectedItemForAdd(item);
  };

  const handleCartAddConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItemForAdd) return;

    setCart(prev => {
      const existing = prev.find(i => 
        i.id === selectedItemForAdd.id && 
        i.warehouseId === addItemForm.warehouseId &&
        i.lotNumber === addItemForm.lotNumber &&
        i.discount === addItemForm.discount &&
        i.discountType === addItemForm.discountType
      );
      const qtyDelta = (isReturn ? -1 : 1) * addItemForm.quantity;
      if (existing) {
        return prev.map(i => (
          i.id === existing.id && 
          i.warehouseId === existing.warehouseId && 
          i.lotNumber === existing.lotNumber &&
          i.discount === existing.discount &&
          i.discountType === existing.discountType
        ) 
          ? { ...i, quantity: i.quantity + qtyDelta } 
          : i
        );
      }
      return [...prev, { 
        ...selectedItemForAdd, 
        quantity: qtyDelta, 
        discount: addItemForm.discount, 
        discountType: addItemForm.discountType, 
        notes: '',
        warehouseId: addItemForm.warehouseId,
        warehouseName: addItemForm.warehouseName,
        lotNumber: addItemForm.lotNumber,
        expiryDate: addItemForm.expiryDate,
        unitPrice: addItemForm.unitPrice
      }];
    });
    setSelectedItemForAdd(null);
  };

  const addCustomItem = () => {
    if (!customItemName || !customSalePrice) return;
    
    const qty = isReturn ? -1 : 1;
    const newItem: CartItem = {
      id: `custom-${Date.now()}`,
      code: 'CUSTOM',
      name: customItemName,
      description: 'Custom Item',
      unitPrice: parseFloat(customSalePrice) || 0,
      quantity: qty,
      discount: 0,
      discountType: 'percent',
      notes: '',
      type: 'service',
      status: 'active',
      enablePOS: true
    };

    setCart(prev => [...prev, newItem]);

    // Reset fields
    setCustomItemName('');
    setCustomCostPrice('');
    setCustomSalePrice('');
  };

  const handleBarcodeScan = (code: string) => {
    const item = items.find(i => 
      i.code.toLowerCase() === code.toLowerCase() || 
      (i.barcode && i.barcode.toLowerCase() === code.toLowerCase())
    );
    if (item) {
      addToCart(item);
      setBarcodeInput('');
      // Keep showBarcodeInput(true) to allow multiple scans
    }
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContactName || isAddingContact) return;

    setIsAddingContact(true);
    try {
      const docRef = await addDoc(collection(db, 'contacts'), {
        name: newContactName,
        phone: newContactPhone,
        type: 'customer',
        orgId,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid
      });
      setSelectedContactId(docRef.id);
      setNewContactName('');
      setNewContactPhone('');
      setShowAddContact(false);
    } catch (error) {
      console.error("Error adding contact:", error);
    } finally {
      setIsAddingContact(false);
    }
  };

  const removeFromCart = (itemId: string, warehouseId?: string, lotNumber?: string, discountValue?: number, discountType?: string) => {
    setCart(prev => prev.filter(i => !(i.id === itemId && i.warehouseId === warehouseId && i.lotNumber === lotNumber && i.discount === discountValue && i.discountType === discountType)));
  };

  const updateQuantity = (itemId: string, warehouseId: string | undefined, lotNumber: string | undefined, delta: number, discountValue?: number, discountType?: string) => {
    setCart(prev => prev.map(i => {
      if (i.id === itemId && i.warehouseId === warehouseId && i.lotNumber === lotNumber && i.discount === discountValue && i.discountType === discountType) {
        const newQty = isReturn ? (i.quantity + delta) : Math.max(1, i.quantity + delta);
        return { ...i, quantity: newQty };
      }
      return i;
    }));
  };

  const updateCartItem = (itemId: string, warehouseId: string | undefined, lotNumber: string | undefined, updates: Partial<CartItem>) => {
    setCart(prev => prev.map(i => (i.id === itemId && i.warehouseId === warehouseId && i.lotNumber === lotNumber) ? { ...i, ...updates } : i));
  };

  const parseTaxRate = (rate?: string) => {
    if (!rate) return 0;
    const val = parseFloat(String(rate || '').replace('%', ''));
    return isNaN(val) ? 0 : val;
  };

  const handleLotChange = (lotNo: string) => {
    setSaleLotNumber(lotNo);
    const lot = lotNumbers.find(l => l.lotNumber === lotNo);
    if (lot && lot.expiryDate) {
      setSaleExpiryDate(lot.expiryDate);
    }
  };

  const calculateItemTotal = (item: CartItem) => {
    const unitPrice = isNaN(item.unitPrice) ? 0 : item.unitPrice;
    const quantity = isNaN(item.quantity) ? 0 : item.quantity;
    const discount = isNaN(item.discount || 0) ? 0 : (item.discount || 0);
    
    let itemTotal = unitPrice * quantity;
    if (discount) {
      if (item.discountType === 'percent') {
        itemTotal -= (itemTotal * discount) / 100;
      } else {
        // For amount discount, we need to be careful with negative quantities
        const sign = quantity < 0 ? -1 : 1;
        itemTotal -= discount * sign;
      }
    }
    return isNaN(itemTotal) ? 0 : itemTotal;
  };

  const calculateItemTax = (item: CartItem) => {
    const itemTotal = calculateItemTotal(item);
    const taxRate = parseTaxRate(item.taxRate);
    return (itemTotal * taxRate) / 100;
  };

  const subtotalRaw = cart.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  const subtotal = isNaN(subtotalRaw) ? 0 : subtotalRaw;
  
  const totalTaxRaw = cart.reduce((sum, item) => sum + calculateItemTax(item), 0);
  const totalTax = isNaN(totalTaxRaw) ? 0 : totalTaxRaw;
  
  const calculateOrderDiscount = () => {
    if (!orderDiscount) return 0;
    if (orderDiscountType === 'percent') {
      return (subtotal * orderDiscount) / 100;
    }
    return orderDiscount;
  };

  const totalRaw = subtotal - calculateOrderDiscount() + totalTax;
  const total = isNaN(totalRaw) ? 0 : totalRaw;

  const handleClearAll = () => {
    setCart([]);
    setSelectedContactId('');
    setOrderNotes('');
    setOrderDiscount(0);
    setOrderDiscountType('percent');
    setAlreadyPaid(0);
    setResumedInvoiceId(null);
    setResumedInvoiceNumber(null);
    if (onClear) onClear();
  };

  const handlePrintReceipt = async (receiptData: any) => {
    let storeData: any = null;
    if (receiptData.storeId) {
      try {
        if (isLocalSession()) {
          storeData = stores.find(s => s.id === receiptData.storeId) || null;
        } else {
          const storeDoc = await getDoc(doc(db, 'stores', receiptData.storeId));
          if (storeDoc.exists()) {
            storeData = storeDoc.data();
          }
        }
      } catch (error) {
        console.error("Error fetching store data for receipt:", error);
      }
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      notify.error('Please allow popups to print receipts');
      return;
    }

    const itemsHtml = receiptData.items.map((item: any) => `
      <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
        <div>
          <div>${item.description || item.name}</div>
          <div style="font-size: 12px; color: #666;">Qty: ${item.quantity} x ${receiptData.currency} ${(item.unitPrice || 0).toFixed(2)}</div>
        </div>
        <div>${receiptData.currency} ${(item.total || 0).toFixed(2)}</div>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt ${receiptData.number}</title>
        <style>
          body { font-family: monospace; padding: 20px; max-width: 300px; margin: 0 auto; color: #141414; }
          .header { text-align: center; margin-bottom: 20px; }
          .logo { max-width: 100px; max-height: 100px; margin-bottom: 10px; }
          .divider { border-top: 1px dashed #000; margin: 10px 0; }
          .total { font-weight: bold; font-size: 1.2em; margin-top: 10px; }
          .footer { text-align: center; margin-top: 20px; font-size: 12px; }
          .terms { margin-top: 15px; font-size: 10px; color: #666; border-top: 1px solid #eee; pt: 10px; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${storeData?.logoUrl ? `<img src="${storeData.logoUrl}" class="logo" />` : ''}
          <div style="font-weight: bold; font-size: 1.2em;">${storeData?.name || 'STORE RECEIPT'}</div>
          ${storeData?.address ? `<div style="font-size: 12px;">${storeData.address}</div>` : ''}
          ${storeData?.socialMediaLink ? `<div style="font-size: 12px; color: #666;">${storeData.socialMediaLink}</div>` : ''}
          <div class="divider"></div>
          <h2>RECEIPT</h2>
          <div>Order #${receiptData.number}</div>
          <div>${new Date().toLocaleString()}</div>
        </div>
        <div class="divider"></div>
        ${itemsHtml}
        <div class="divider"></div>
        <div style="display: flex; justify-content: space-between;">
          <span>Subtotal</span>
          <span>${receiptData.currency} ${(receiptData.subtotal || 0).toFixed(2)}</span>
        </div>
        <div style="display: flex; justify-content: space-between;">
          <span>Tax</span>
          <span>${receiptData.currency} ${(receiptData.totalTax || 0).toFixed(2)}</span>
        </div>
        <div style="display: flex; justify-content: space-between;" class="total">
          <span>Total</span>
          <span>${receiptData.currency} ${(receiptData.totalAmount || 0).toFixed(2)}</span>
        </div>
        <div class="divider"></div>
        <div class="footer">
          ${storeData?.termsAndConditions ? `<div class="terms">${storeData.termsAndConditions}</div>` : ''}
        </div>
        <script>
          window.onload = () => {
            window.print();
            setTimeout(() => window.close(), 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(localizePrintableHtml(html));
    printWindow.document.close();
  };

  const [lastReceipt, setLastReceipt] = useState<any>(null);
  const [lastInvoiceId, setLastInvoiceId] = useState<string | null>(null);

  const handleCheckout = async (status: 'Paid' | 'Parked' | 'Awaiting Payment' = 'Paid', paymentMethod: string = '', bankAccountId?: string, paidAmount?: number) => {
    const localAuthToken = localStorage.getItem('app_auth_token');
    if (cart.length === 0 || (!auth.currentUser && !localAuthToken)) return;
    
    // Check if customer is required
    if (selectedStoreId) {
      const store = stores.find(s => s.id === selectedStoreId);
      if (store?.enforceCustomerOnCheckout && !selectedContactId) {
        notify.error('Please select a customer to complete the checkout.');
        return;
      }
    }

    // Determine the actual status based on payment amount
    // If we are in "Check-out" mode (status === 'Paid') but the paid amount is less than total,
    // it should be 'Parked'
    const finalStatus: 'Paid' | 'Parked' | 'Awaiting Payment' = (status === 'Paid' && paidAmount !== undefined && Math.abs((paidAmount || 0) + alreadyPaid) < Math.abs(total)) 
      ? 'Parked' 
      : status;
    
    setIsCheckingOut(true);
    try {
      if (localAuthToken) {
        try {
          const store = stores.find(s => s.id === selectedStoreId) || stores.find(s => s.status === 'active') || stores[0];
          const storeId = selectedStoreId || store?.id || '';
          const receiptNumber = resumedInvoiceNumber || (store ? `${store.posPrefix || 'Receipt Number-'}${store.nextPosNumber || 1}` : `POS-${Math.floor(1000 + Math.random() * 9000)}`);
          const effectivePaymentMethod = (finalStatus === 'Parked' || finalStatus === 'Awaiting Payment') ? 'None' : (paymentMethod || 'Cash');
          const paymentAmount = paidAmount ?? total;
          const payments = finalStatus === 'Paid'
            ? [{ paymentMethod: effectivePaymentMethod, amount: paymentAmount, accountId: bankAccountId || undefined }]
            : [];
          const saleItems = cart.map(item => {
            const lotId = item.lotNumber || saleLotNumber;
            const cost = item.cost ?? item.costPrice;
            return {
              itemId: item.id,
              quantity: item.quantity,
              ...(lotId ? { lotId } : {}),
              ...(cost !== undefined ? { cost } : {})
            };
          });

          const response = await fetch(`/api/organizations/${orgId}/pos-sales`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localAuthToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              storeId,
              ...(selectedContactId ? { contactId: selectedContactId } : {}),
              number: receiptNumber,
              subtotal,
              totalAmount: total,
              taxAmount: totalTax,
              paymentMethod: effectivePaymentMethod,
              payments,
              items: saleItems
            })
          });

          if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(error?.error || error?.message || 'Unable to complete sale.');
          }

          const sale = await response.json().catch(() => null);
          setLastReceipt({
            number: receiptNumber,
            storeId,
            items: cart.map(item => ({
              description: item.name,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              total: calculateItemTotal(item)
            })),
            subtotal,
            totalTax,
            totalAmount: total,
            currency: getDefaultCurrency(orgSettings)
          });
          setLastInvoiceId(sale?.id || sale?.sale?.id || null);
          setCart([]);
          setSelectedContactId('');
          setOrderNotes('');
          setOrderDiscount(0);
          setAlreadyPaid(0);
          setResumedInvoiceId(null);
          setResumedInvoiceNumber(null);
          setCheckoutSuccess(true);
          setShowCheckoutModal(false);
          setSaleLotNumber('');
          setSaleExpiryDate('');
          if (finalStatus === 'Parked' || finalStatus === 'Awaiting Payment') {
            notify.confirm(`Sale parked: ${receiptNumber}`);
          } else {
            notify.confirm(`Sale completed: ${receiptNumber}`);
          }
          setTimeout(() => setCheckoutSuccess(false), 3000);
        } catch (error) {
          notify.error(error instanceof Error ? error.message : 'Unable to complete sale.');
        }
        return;
      }

      let receiptNumber = resumedInvoiceNumber || `POS-${Math.floor(1000 + Math.random() * 9000)}`;
      
      if (!resumedInvoiceNumber && selectedStoreId) {
        const store = stores.find(s => s.id === selectedStoreId);
        if (store) {
          let nextNum = store.nextPosNumber || 1;
          receiptNumber = `${store.posPrefix || 'Receipt Number-'}${nextNum}`;
          
          // Increment next number in store document
          await updateDoc(doc(db, 'stores', selectedStoreId), {
            nextPosNumber: increment(1)
          });
          
          // Update local stores state
          setStores(prev => prev.map(s => s.id === selectedStoreId ? { ...s, nextPosNumber: nextNum + 1 } : s));
        }
      }

      // Create a transaction record
      const discountAmount = calculateOrderDiscount();
      const transactionItems = cart.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount || 0,
        discountType: item.discountType || 'percent',
        notes: item.notes || '',
        taxRate: item.taxRate || '0%',
        taxAmount: calculateItemTax(item),
        total: calculateItemTotal(item)
      }));

      if (discountAmount > 0) {
        transactionItems.push({
          id: 'discount',
          name: `Invoice Full Discount (${orderDiscountType === 'percent' ? '%' : 'Amt'})`,
          quantity: 1,
          unitPrice: -discountAmount,
          discount: 0,
          discountType: 'amount',
          notes: '',
          taxRate: '0%',
          taxAmount: 0,
          total: -discountAmount
        });
      }

      let storesCategory = trackingCategories.find(c => c.name === 'Stores');
      const storeName = stores.find(s => s.id === selectedStoreId)?.name;
      
      if (!storesCategory && storeName) {
        // Create it on the fly
        const activeStoreNames = stores.filter(s => s.status === 'active').map(s => s.name);
        try {
          const docRef = await addDoc(collection(db, 'tracking_categories'), {
            orgId,
            name: 'Stores',
            options: activeStoreNames,
            status: 'active',
            createdBy: auth.currentUser?.uid || 'system',
            createdAt: serverTimestamp()
          });
          storesCategory = { id: docRef.id, name: 'Stores', options: activeStoreNames } as any;
        } catch (error) {
          console.error('Error creating Stores tracking category:', error);
        }
      } else if (storesCategory && storeName && !storesCategory.options.includes(storeName)) {
        // Add missing store to options
        try {
          const newOptions = [...storesCategory.options, storeName];
          await updateDoc(doc(db, 'tracking_categories', storesCategory.id), {
            options: newOptions,
            updatedAt: serverTimestamp()
          });
          storesCategory.options = newOptions;
        } catch (error) {
          console.error('Error updating Stores tracking category options:', error);
        }
      }

      const trackingCategoryValue = (storesCategory && storeName)
        ? `${storesCategory.id}:${storeName}`
        : '';

      const documentItems = cart.map(item => ({
        item: item.id,
        description: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount || 0,
        taxRate: item.taxRate || '0%',
        taxAmount: calculateItemTax(item),
        total: calculateItemTotal(item),
        account: item.account || '',
        trackingCategory: trackingCategoryValue
      }));

      if (discountAmount > 0) {
        documentItems.push({
          item: 'discount',
          description: `Invoice Full Discount (${orderDiscountType === 'percent' ? '%' : 'Amt'})`,
          quantity: 1,
          unitPrice: -discountAmount,
          discount: 0,
          taxRate: '0%',
          taxAmount: 0,
          total: -discountAmount,
          account: '',
          trackingCategory: trackingCategoryValue
        });
      }

      const invoiceData = {
        number: receiptNumber,
        orgId,
        storeId: selectedStoreId,
        type: 'RECEIPT',
        source: 'POS',
        contactId: selectedContactId || 'walk-in',
        contactName: selectedContactId ? (contacts.find(c => c.id === selectedContactId)?.name || 'Walk-in Customer') : 'Walk-in Customer',
        contactPhone: selectedContactId ? (contacts.find(c => c.id === selectedContactId)?.phone || '') : '',
        lotNumber: saleLotNumber,
        expiryDate: saleExpiryDate,
        date: new Date().toISOString().split('T')[0],
        dueDate: new Date().toISOString().split('T')[0],
        currency: getDefaultCurrency(orgSettings),
        subtotal,
        totalTax,
        orderDiscount: discountAmount,
        orderDiscountType,
        orderDiscountValue: orderDiscount,
        totalAmount: total,
        status: finalStatus,
        notes: orderNotes,
        time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
        items: documentItems,
        paymentReceived: status === 'Paid' ? ((paidAmount || total) + alreadyPaid) : alreadyPaid,
        paymentDate: (status === 'Paid' || (paidAmount && paidAmount > 0) || alreadyPaid > 0) ? new Date().toISOString().split('T')[0] : '',
        paymentAccount: bankAccountId || '',
        paymentMethod: (finalStatus === 'Parked' || finalStatus === 'Awaiting Payment') ? 'None' : (paymentMethod || 'Cash'),
        createdBy: auth.currentUser?.uid || 'unknown',
        createdByName: auth.currentUser?.displayName || 'Unknown User',
        updatedAt: serverTimestamp(),
      };

      let invoiceId = resumedInvoiceId;

      if (resumedInvoiceId) {
        await updateDoc(doc(db, 'documents', resumedInvoiceId), {
          ...invoiceData,
          actionDetails: 'Updated via POS',
          actionLog: arrayUnion({
            action: status === 'Paid' ? 'Payment Received' : 'Updated',
            user: auth.currentUser.displayName || 'Unknown User',
            email: auth.currentUser.email || 'No Email',
            timestamp: new Date().toISOString(),
            details: status === 'Paid' ? `Payment of ${paidAmount} received via POS` : 'Document updated via Point of Sale'
          })
        });
        await logHistory(orgId, status === 'Paid' ? 'POS Sale Completed' : 'POS Sale Updated', status === 'Paid' ? `Payment of ${paidAmount} received via POS` : 'Document updated via Point of Sale', auth.currentUser, resumedInvoiceId, 'Invoice');
      } else {
        // Create an invoice document in the 'documents' collection
        const docRef = await addDoc(collection(db, 'documents'), {
          ...invoiceData,
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || 'Unknown User',
          createdByEmail: auth.currentUser.email || 'No Email',
          createdAt: serverTimestamp(),
          actionDetails: 'Created via POS',
          actionLog: [{
            action: 'Created',
            user: auth.currentUser.displayName || 'Unknown User',
            email: auth.currentUser.email || 'No Email',
            timestamp: new Date().toISOString(),
            details: 'Document created via Point of Sale'
          }]
        });
        invoiceId = docRef.id;
        await logHistory(orgId, 'POS Sale Created', `Sale #${receiptNumber} created via POS`, auth.currentUser, invoiceId, 'Invoice');
      }

      // Deduct stock and record movements for products in the cart
      // Only for finalized sales (Paid or Awaiting Payment)
      if (finalStatus !== 'Parked') {
        for (const item of cart) {
          if (item.type === 'product') {
            try {
              const itemRef = doc(db, 'items', item.id);
              const itemSnap = await getDoc(itemRef);
              
              if (itemSnap.exists()) {
                const itemData = itemSnap.data() as Item;
                const targetWarehouseId = item.warehouseId || selectedStoreId;
                
                // Check for active recipe
                const activeRecipe = itemData.recipeManagement ? itemData.recipes?.find(r => r.isActive) : null;
                
                if (activeRecipe && activeRecipe.components.length > 0) {
                  // Deduct recipe components
                  for (const component of activeRecipe.components) {
                    const totalDeduction = component.quantity * item.quantity;
                    await reduceStockFIFO(component.itemId, totalDeduction, targetWarehouseId);

                    await addDoc(collection(db, 'stock_movements'), {
                      itemId: component.itemId,
                      storeId: targetWarehouseId,
                      type: isReturn ? 'return' : 'sale',
                      quantity: -totalDeduction,
                      referenceId: invoiceId,
                      referenceNumber: receiptNumber,
                      createdBy: auth.currentUser?.uid,
                      createdByName: auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Unknown User',
                      createdAt: serverTimestamp(),
                      orgId: orgId,
                      note: `Recipe component for ${item.name}`
                    });
                  }
                } else {
                  // Standard stock deduction for the item itself
                  await reduceStockFIFO(item.id, item.quantity, targetWarehouseId);
                  
                  // Record movement
                  await addDoc(collection(db, 'stock_movements'), {
                    itemId: item.id,
                    storeId: targetWarehouseId,
                    type: isReturn ? 'return' : 'sale',
                    quantity: -item.quantity,
                    lotNumber: (item.lotNumber || saleLotNumber) || '',
                    expiryDate: (item.expiryDate || saleExpiryDate) || '',
                    referenceId: invoiceId,
                    referenceNumber: receiptNumber,
                    createdBy: auth.currentUser?.uid,
                    createdByName: auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Unknown User',
                    createdAt: serverTimestamp(),
                    orgId: orgId
                  });
                }
              }
            } catch (error) {
              handleFirestoreError(error, OperationType.WRITE, 'stock_movements');
            }
          }
        }
      }

      // Create a transaction record if paid
      if (status === 'Paid' && invoiceId) {
        const transactionData = {
          orgId,
          storeId: selectedStoreId,
          date: new Date().toISOString(),
          description: `POS Sale: ${cart.length} items (${receiptNumber})`,
          category: 'Sales',
          subtotal,
          orderDiscount: discountAmount,
          orderDiscountType,
          orderDiscountValue: orderDiscount,
          amount: paidAmount || total,
          currency: getDefaultCurrency(orgSettings),
          status: 'Completed',
          paymentMethod: paymentMethod,
          accountId: bankAccountId || '',
          reference: receiptNumber,
          contactId: selectedContactId || 'walk-in',
          contactName: selectedContactId ? contacts.find(c => c.id === selectedContactId)?.name : 'Walk-in Customer',
          notes: orderNotes,
          items: transactionItems,
          invoiceId: invoiceId, // Link to the invoice
          createdBy: auth.currentUser.uid,
          createdAt: serverTimestamp()
        };
        const txDoc = await addDoc(collection(db, 'transactions'), transactionData);

        // Accounting: for a normal sale, post the customer payment (Debit Cash / Credit AR).
        // Returns post their own Sales-Return + Cash-Payout journals below.
        if (!isReturn) {
          await postPaymentJournal(orgId, { ...transactionData, id: txDoc.id }, { ...invoiceData, id: invoiceId }, auth.currentUser.uid, 'invoice');
        }
      }

      // Accounting: Post Sales and Inventory (COGS) Journals if confirmed
      if (finalStatus !== 'Parked' && invoiceId) {
        const fullInvoice = { ...invoiceData, id: invoiceId };
        if (isReturn) {
          // Per SYSTEM_DOCUMENTATION §4.D, a POS return posts Sales Return and Cash
          // Payout GL journals (not negated sale journals). Build a positive-valued
          // credit note from the return cart (which holds negative quantities/totals).
          const returnTotal = Math.abs(total);
          const creditNote = {
            ...fullInvoice,
            totalAmount: returnTotal,
            items: (documentItems || []).map((it: any) => ({
              ...it,
              total: Math.abs(it.total || 0),
              taxAmount: Math.abs(it.taxAmount || 0),
            })),
          };
          await postCreditNoteJournal(orgId, creditNote, auth.currentUser.uid);

          // Cash payout to the customer: Credit Cash / Debit AR for the refunded amount.
          if (status === 'Paid' && returnTotal > 0) {
            const uid = auth.currentUser.uid;
            const cashAccount = (bankAccountId ? await getAccountById(bankAccountId) : null)
              || await ensureAccountByCode(orgId, '1020', { name: 'Bank - Operating Account', type: 'Bank', nature: 'Debit', enablePayments: true }, uid);
            const arAccount = await ensureAccountByCode(orgId, '1100', { name: 'Accounts Receivable', type: 'Current Asset', nature: 'Debit', taxRate: '0%' }, uid);
            await createAutoJournal({
              orgId,
              date: fullInvoice.date,
              narration: `POS Refund Payout ${receiptNumber}`,
              reference: receiptNumber,
              sourceType: 'payment',
              sourceId: invoiceId,
              status: 'Posted',
              lines: [
                { accountId: arAccount.id, accountName: arAccount.name, description: `Refund clearing ${receiptNumber}`, debit: returnTotal, credit: 0 },
                { accountId: cashAccount.id, accountName: cashAccount.name, description: `Cash paid out for return ${receiptNumber}`, debit: 0, credit: returnTotal },
              ],
              totalDebit: returnTotal,
              totalCredit: returnTotal,
              createdBy: uid,
            });
          }
          // Note: restocking is handled by the negative-quantity stock movement above,
          // which increases on-hand quantity for the returned items.
        } else {
          await postInvoiceJournal(orgId, fullInvoice, auth.currentUser.uid);
          await postInventoryJournal(orgId, fullInvoice, auth.currentUser.uid);
        }
      }

      setLastReceipt(invoiceData);
      setLastInvoiceId(invoiceId);
      setCart([]);
      setSelectedContactId('');
      setOrderNotes('');
      setOrderDiscount(0);
      setAlreadyPaid(0);
      setResumedInvoiceId(null);
      setResumedInvoiceNumber(null);
      setCheckoutSuccess(true);
      setShowCheckoutModal(false);
      setSaleLotNumber('');
      setSaleExpiryDate('');
      if (finalStatus === 'Parked' || finalStatus === 'Awaiting Payment') {
        notify.confirm(`Sale parked: ${receiptNumber}`);
      } else {
        notify.confirm(`Sale completed: ${receiptNumber}`);
      }
      setTimeout(() => setCheckoutSuccess(false), 3000);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'documents');
    } finally {
      setIsCheckingOut(false);
    }
  };

  const resumeParkedInvoice = async (invoice: any) => {
    // Map document items back to cart items
    const cartItems: CartItem[] = (invoice.items || [])
      .filter((item: any) => item.item !== 'discount')
      .map((item: any) => {
        const originalItem = items.find(i => i.id === item.item);
        return {
          ...(originalItem || {}),
          id: item.item,
          code: originalItem?.code || 'ITEM',
          name: item.description || originalItem?.name || 'Unknown Item',
          description: item.description || originalItem?.description || '',
          unitPrice: item.unitPrice,
          quantity: item.quantity,
          discount: item.discount || 0,
          discountType: 'amount',
          taxRate: item.taxRate || originalItem?.taxRate || '0%',
          type: originalItem?.type || 'product',
          status: originalItem?.status || 'active',
          enablePOS: true
        } as CartItem;
      });

    setCart(cartItems);
    setSelectedContactId(invoice.contactId === 'walk-in' ? '' : (invoice.contactId || ''));
    setOrderNotes(invoice.notes || '');
    setSaleLotNumber(invoice.lotNumber || '');
    setSaleExpiryDate(invoice.expiryDate || '');
    setOrderDiscount(invoice.orderDiscountValue || 0);
    setOrderDiscountType(invoice.orderDiscountType || 'percent');
    setAlreadyPaid(invoice.paymentReceived || 0);
    setResumedInvoiceId(invoice.id);
    setResumedInvoiceNumber(invoice.number);
    setShowParkedModal(false);
    notify.confirm(`Resumed sale: ${invoice.number}`);
  };

  const prepareReturn = (invoice: any) => {
    // Map document items back to cart items with negative quantities
    const cartItems: CartItem[] = (invoice.items || [])
      .filter((item: any) => item.item !== 'discount')
      .map((item: any) => {
        const originalItem = items.find(i => i.id === item.item);
        return {
          ...(originalItem || {}),
          id: item.item,
          code: originalItem?.code || 'ITEM',
          name: item.description || originalItem?.name || 'Unknown Item',
          description: item.description || originalItem?.description || '',
          unitPrice: item.unitPrice,
          quantity: -Math.abs(item.quantity),
          discount: item.discount || 0,
          discountType: 'amount',
          taxRate: item.taxRate || originalItem?.taxRate || '0%',
          type: originalItem?.type || 'product',
          status: originalItem?.status || 'active',
          enablePOS: true
        } as CartItem;
      });

    setCart(cartItems);
    setSelectedContactId(invoice.contactId === 'walk-in' ? '' : (invoice.contactId || ''));
    setOrderNotes(`Return for ${invoice.number}`);
    setOrderDiscount(invoice.orderDiscountValue || 0);
    setOrderDiscountType(invoice.orderDiscountType || 'percent');
    setAlreadyPaid(0);
    setResumedInvoiceId(null); // Create new document for return
    setResumedInvoiceNumber(`${invoice.number}/return`);
    setShowParkedModal(false);
    notify.confirm(`Prepared return for: ${invoice.number}`);
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 pb-20 pt-4">
      {/* Store Selector Modal */}
      {showStoreSelector && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowStoreSelector(false);
          }}
        >
          <div className="bg-card text-foreground w-full max-w-md rounded-[2.5rem] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 border border-border">
            <div className="p-8 space-y-8">
              <div>
                <h2 className="text-2xl font-bold italic serif">Select Store</h2>
                <p className="text-xs opacity-40 mt-1">Please select the physical store you are operating from.</p>
              </div>
              <div className="space-y-3">
                {stores.map(store => (
                  <button
                    key={store.id}
                    onClick={() => {
                      setSelectedStoreId(store.id);
                      setShowStoreSelector(false);
                    }}
                    className={cn(
                      "w-full p-6 text-left rounded-[2rem] transition-all border font-black uppercase text-xs tracking-widest flex items-center justify-between group",
                      selectedStoreId === store.id 
                        ? "bg-[#141414] text-white border-[#141414] shadow-xl" 
                        : "bg-white border-[#141414]/10 hover:border-[#141414] hover:shadow-lg"
                    )}
                  >
                    <span>{store.name}</span>
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-transform",
                      selectedStoreId === store.id ? "translate-x-0" : "opacity-0 group-hover:opacity-100 group-hover:translate-x-1"
                    )} />
                  </button>
                ))}
                
                {selectedStoreId && (
                  <button
                    onClick={() => setShowStoreSelector(false)}
                    className="w-full p-4 text-center text-[#141414]/40 hover:text-[#141414] transition-colors font-black uppercase text-[10px] tracking-widest mt-4"
                  >
                    Keep Current Selection
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Left Side: Product Selection */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex gap-6">
          {/* Items Grid Container (The "Bigger Box") */}
          <div className="flex-1 flex flex-col bg-white/30 border border-[#141414]/10 rounded-3xl">
            {/* Search Bar & Custom Item inside the bigger box */}
            <div className="p-4 border-b border-[#141414]/10 bg-white/20">
              <div className="flex flex-wrap items-end gap-3">
                {/* Smaller Search Bar */}
                <div className="flex-1 min-w-[200px]">
                  <label className="text-[10px] font-bold uppercase tracking-wider opacity-40 mb-1 block">Search Products</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-50" />
                    <input 
                      type="text" 
                      placeholder="Search or scan..." 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-white/50 border border-[#141414]/20 pl-10 pr-4 py-2 rounded-xl outline-none focus:ring-2 ring-[#141414]/5 transition-all text-xs font-bold"
                    />
                  </div>
                </div>

                {/* Custom Item Section */}
                <div className={cn(
                  "flex items-end gap-2 transition-all duration-300",
                  showCustomItemForm ? "flex-[3] min-w-[500px]" : "flex-none"
                )}>
                  {!showCustomItemForm && !showBarcodeInput && (
                    <button 
                      onClick={() => setShowCustomItemForm(true)}
                      className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-xl hover:opacity-90 transition-all font-bold text-xs h-[38px]"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Custom Item</span>
                    </button>
                  )}
                  {showCustomItemForm && (
                    <div className="flex items-end gap-2 bg-[#141414]/5 p-2 rounded-2xl border border-[#141414]/10 w-full animate-in fade-in slide-in-from-right-4 duration-300">
                      <div className="flex-1">
                        <label className="text-[9px] font-bold uppercase tracking-wider opacity-40 mb-1 block">Custom Item Name</label>
                        <input 
                          type="text" 
                          placeholder="Product Name" 
                          value={customItemName}
                          onChange={(e) => setCustomItemName(e.target.value)}
                          className="w-full bg-white border border-[#141414]/10 px-3 py-2 rounded-lg outline-none text-xs font-bold"
                          autoFocus
                        />
                      </div>
                      <div className="w-20">
                        <label className="text-[9px] font-bold uppercase tracking-wider opacity-40 mb-1 block">Cost</label>
                        <input 
                          type="number" 
                          placeholder="0.00" 
                          value={isNaN(parseFloat(customCostPrice)) ? '' : customCostPrice}
                          onChange={(e) => setCustomCostPrice(e.target.value)}
                          className="w-full bg-white border border-[#141414]/10 px-3 py-2 rounded-lg outline-none text-xs font-mono font-bold"
                        />
                      </div>
                      <div className="w-20">
                        <label className="text-[9px] font-bold uppercase tracking-wider opacity-40 mb-1 block">Sale</label>
                        <input 
                          type="number" 
                          placeholder="0.00" 
                          value={isNaN(parseFloat(customSalePrice)) ? '' : customSalePrice}
                          onChange={(e) => setCustomSalePrice(e.target.value)}
                          className="w-full bg-white border border-[#141414]/10 px-3 py-2 rounded-lg outline-none text-xs font-mono font-bold"
                        />
                      </div>
                      <div className="flex gap-1">
                        <button 
                          onClick={addCustomItem}
                          disabled={!customItemName || !customSalePrice}
                          className="bg-[#141414] text-[#E4E3E0] p-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-30"
                          title="Add to Cart"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => setShowCustomItemForm(false)}
                          className="bg-white border border-[#141414]/10 text-[#141414] p-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-all"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Scan Barcode Section */}
                <div className={cn(
                  "flex items-end gap-2 transition-all duration-300",
                  showBarcodeInput ? "flex-[1] min-w-[250px]" : "flex-none"
                )}>
                  {!showBarcodeInput && !showCustomItemForm && (
                    <button 
                      onClick={() => setShowBarcodeInput(true)}
                      className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-xl hover:opacity-90 transition-all font-bold text-xs h-[38px]"
                    >
                      <Barcode className="w-4 h-4" />
                      <span>Scan Barcode</span>
                    </button>
                  )}

                  {/* Parked Invoices Button */}
                  {!showBarcodeInput && !showCustomItemForm && (
                    <button 
                      onClick={() => setShowParkedModal(true)}
                      className="flex items-center gap-2 bg-white border border-[#141414]/10 text-[#141414] px-4 py-2 rounded-xl hover:bg-[#141414] hover:text-[#E4E3E0] transition-all font-bold text-xs h-[38px] relative"
                    >
                      <Pause className="w-4 h-4" />
                      <span>Parked Invoices</span>
                      {parkedInvoices.length > 0 && (
                        <span className="absolute -bottom-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full animate-pulse border-2 border-[#E4E3E0]">
                          {parkedInvoices.length}
                        </span>
                      )}
                    </button>
                  )}
                  {showBarcodeInput && (
                    <div className="flex items-end gap-2 bg-[#141414]/5 p-2 rounded-2xl border border-[#141414]/10 w-full animate-in fade-in slide-in-from-right-4 duration-300">
                      <div className="flex-1">
                        <label className="text-[9px] font-bold uppercase tracking-wider opacity-40 mb-1 block">Enter Barcode / SKU</label>
                        <div className="relative">
                          <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-50" />
                          <input 
                            type="text" 
                            placeholder="Barcode..." 
                            value={barcodeInput}
                            onChange={(e) => setBarcodeInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleBarcodeScan(barcodeInput);
                              }
                            }}
                            className="w-full bg-card border border-border pl-10 pr-3 py-2 rounded-lg outline-none text-xs font-bold text-foreground"
                            autoFocus
                          />
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowBarcodeInput(false)}
                        className="bg-card border border-border text-foreground p-2 rounded-lg hover:bg-red-50 hover:text-red-600 transition-all"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-4 pb-4">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin opacity-20" />
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center opacity-50 italic serif">
                  <Package className="w-12 h-12 mb-4 opacity-20" />
                  <p>No POS-enabled items found.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pb-4">
                  {filteredItems.map(item => (
                    <button
                      key={item.id}
                      onClick={() => addToCart(item)}
                      disabled={item.type === 'product' && (item.storeStock?.[selectedStoreId] || 0) <= 0 && !item.allowNegativeStock}
                      className={cn(
                        "group flex flex-col text-left bg-card border border-border rounded-2xl p-3 hover:bg-primary hover:text-primary-foreground transition-all relative overflow-hidden h-40 shadow-sm hover:shadow-lg",
                        item.type === 'product' && (item.storeStock?.[selectedStoreId] || 0) <= 0 && !item.allowNegativeStock && "opacity-40 grayscale cursor-not-allowed"
                      )}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider",
                          item.type === 'product' ? "bg-primary/10 text-primary group-hover:bg-white/20 group-hover:text-white" : "bg-purple-100 text-purple-600 group-hover:bg-white/20 group-hover:text-white"
                        )}>
                          {item.type}
                        </span>
                        <div className="flex flex-col items-end">
                          <span className="text-[8px] opacity-40 font-mono font-bold">SKU: {item.code}</span>
                        </div>
                      </div>
                      <h3 className="font-bold text-sm mb-1 line-clamp-2 leading-tight">{item.name}</h3>
                      
                      {item.type === 'product' && (
                        <div className={cn(
                          "text-[8px] font-bold uppercase tracking-tighter mb-2",
                          (item.storeStock?.[selectedStoreId] || 0) > 0 ? "text-green-500" : (item.allowNegativeStock ? "text-amber-500" : "text-red-500")
                        )}>
                          {(item.storeStock?.[selectedStoreId] || 0) > 0 
                            ? `Stock: ${item.storeStock?.[selectedStoreId]}` 
                            : (item.allowNegativeStock ? `Backorder (${item.storeStock?.[selectedStoreId] || 0})` : "Out of Stock")}
                        </div>
                      )}

                      <div className="mt-auto flex items-center justify-between">
                        <span className="font-mono font-bold text-sm text-foreground">{getDefaultCurrency(orgSettings)}{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground group-hover:bg-primary-foreground group-hover:text-primary flex items-center justify-center transition-colors shadow-md">
                          <Plus className="w-4 h-4" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side: Cart & Checkout */}
      <div className="w-[450px] bg-card border border-border rounded-[2.5rem] flex flex-col shadow-2xl text-foreground sticky top-6 self-start">
        <div className="p-6 border-b border-border space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <Building2 className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-[10px] font-black uppercase tracking-widest opacity-40 leading-none">Operating From</h4>
                <p className="text-xs font-bold leading-none mt-1">{stores.find(s => s.id === selectedStoreId)?.name || 'Select Store'}</p>
              </div>
            </div>
            <button 
              onClick={() => setShowStoreSelector(true)}
              className="px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all"
            >
              Change
            </button>
          </div>

          {isReturn && (
            <div className="bg-red-500 text-white px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 animate-pulse">
              <RotateCcw className="w-3 h-3" />
              Return Mode Active
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <div>
                <h2 className="font-bold italic serif">Customer</h2>
                {resumedInvoiceNumber && (
                  <p className="text-[10px] font-mono opacity-50 -mt-1">#{resumedInvoiceNumber}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleClearAll}
                className="p-1.5 bg-red-500 text-white rounded-lg hover:opacity-90 transition-all flex items-center gap-1.5"
                title="Clear All"
              >
                <Trash2 className="w-4 h-4" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Clear</span>
              </button>
              <button 
                onClick={() => setShowAddContact(!showAddContact)}
                className="p-1.5 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-all"
                title="Add New Customer"
              >
                <UserPlus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showAddContact ? (
            <form onSubmit={handleAddContact} className="space-y-3 bg-muted/20 p-3 rounded-2xl border border-border animate-in fade-in slide-in-from-top-2">
              <div className="space-y-1">
                <label className="text-[9px] font-bold uppercase tracking-wider opacity-40">Customer Name</label>
                <input 
                  type="text" 
                  placeholder="Full Name" 
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  className="w-full bg-card border border-border px-3 py-2 rounded-lg outline-none text-xs font-bold"
                  autoFocus
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-bold uppercase tracking-wider opacity-40">Phone Number</label>
                <input 
                  type="text" 
                  placeholder="Phone" 
                  value={newContactPhone}
                  onChange={(e) => setNewContactPhone(e.target.value)}
                  className="w-full bg-card border border-border px-3 py-2 rounded-lg outline-none text-xs font-mono font-bold"
                />
              </div>
              <div className="flex gap-2">
                <button 
                  type="submit"
                  disabled={isAddingContact || !newContactName}
                  className="flex-1 bg-[#141414] text-[#E4E3E0] py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-30"
                >
                  {isAddingContact ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : 'Save Customer'}
                </button>
                <button 
                  type="button"
                  onClick={() => setShowAddContact(false)}
                  className="px-3 bg-white border border-[#141414]/10 text-[#141414] py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-red-50 hover:text-red-600 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="relative">
              <SearchableSelect 
                value={selectedContactId}
                onChange={(e) => setSelectedContactId(e.target.value)}
                options={[
                  { id: '', label: 'Walk-in Customer' },
                  ...contacts.map(contact => ({
                    id: contact.id,
                    label: contact.name,
                    subLabel: contact.phone || undefined
                  }))
                ]}
                placeholder="Walk-in Customer"
              />
            </div>
          )}
        </div>

        <div className="border-b border-border p-5 bg-muted/40">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] opacity-40">Cart Items</h3>
            <span className="bg-[#141414] text-[#E4E3E0] text-xs font-bold px-3 py-1 rounded-full ring-4 ring-white/10 shadow-lg">
              {cart.reduce((sum, i) => sum + i.quantity, 0)}
            </span>
          </div>
        </div>

        <div className="p-4 space-y-4 bg-muted/10">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center opacity-30 italic serif text-center p-8">
              <ShoppingCart className="w-12 h-12 mb-4 opacity-20" />
              <p>Your cart is empty.<br/>Select items to start selling.</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={`${item.id}-${item.warehouseId}-${item.lotNumber}-${item.discount}-${item.discountType}`} className="bg-background p-4 rounded-3xl border border-border flex flex-col gap-4 shadow-sm hover:border-primary/30 transition-all">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="text-base font-bold leading-tight decoration-primary/20 decoration-2">{item.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs opacity-50 font-mono font-bold">{getDefaultCurrency(orgSettings)}{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / unit</p>
                        <span className="text-[10px] bg-muted px-2 py-0.5 rounded-full font-bold opacity-40">Qty: {item.quantity}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => {
                        setEditingItem(item);
                        setAddItemForm({
                          quantity: Math.abs(item.quantity),
                          discount: item.discount || 0,
                          discountType: item.discountType || 'amount',
                          warehouseId: item.warehouseId || '',
                          warehouseName: item.warehouseName || '',
                          lotNumber: item.lotNumber || 'AUTO',
                          expiryDate: item.expiryDate || '',
                          unitPrice: item.unitPrice || 0
                        });
                      }}
                      className="p-1.5 hover:bg-primary/10 hover:text-primary rounded-lg transition-all"
                      title="Edit Item"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => removeFromCart(item.id, item.warehouseId, item.lotNumber, item.discount || 0, item.discountType || 'amount')}
                      className="p-1.5 hover:bg-destructive/10 hover:text-destructive rounded-lg transition-all"
                      title="Delete Item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {item.notes && (
                  <p className="text-[10px] italic opacity-40 line-clamp-1">Note: {item.notes}</p>
                )}
                {item.discount !== undefined && item.discount > 0 ? (
                  <div className="flex items-center gap-2 text-[10px] text-primary font-bold">
                    <Tag className="w-3 h-3" />
                    <span>Discount: {item.discountType === 'percent' ? `${item.discount}%` : `${getDefaultCurrency(orgSettings)}{(item.discount || 0).toLocaleString()}`} off</span>
                  </div>
                ) : null}

                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold opacity-30">{item.warehouseName || 'Main'}</span>
                      {item.lotNumber && item.lotNumber !== 'AUTO' && (
                        <span className="text-[10px] font-mono opacity-30">| Lot: {item.lotNumber}</span>
                      )}
                  </div>
                  <div className="text-right">
                    {item.discount !== undefined && item.discount > 0 ? (
                      <p className="text-[10px] line-through opacity-30">{getDefaultCurrency(orgSettings)}{((item.unitPrice || 0) * (item.quantity || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    ) : null}
                    <span className="font-mono font-bold text-xl tracking-tight">
                      {calculateItemTotal(item) < 0 ? '-' : ''}
                      {getDefaultCurrency(orgSettings)}
                      {Math.abs(calculateItemTotal(item) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
          
          {/* Order Notes */}
          <div className="mt-6 space-y-2 px-2">
            <label className="text-[10px] font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
              <FileText className="w-3 h-3" />
              Order Notes
            </label>
            <textarea 
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="Add notes to this order..."
              className="w-full bg-white border border-[#141414]/10 p-3 rounded-2xl text-xs font-medium outline-none focus:ring-2 ring-primary/20 transition-all shadow-sm min-h-[80px] resize-none"
            />
          </div>
        </div>

        <div className="p-5 bg-card text-foreground space-y-4 border-t border-border shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.05)] z-10 block">
          <div className="space-y-3">
            {/* Global Order Settings Section */}
            <div className="flex items-center justify-between p-4 bg-muted/50 border border-border rounded-2xl shadow-sm">
              <span className="text-xs font-bold uppercase tracking-widest opacity-60">Global Discount</span>
              <div className="flex items-center gap-3">
                <div className="flex bg-background p-1 rounded-xl border border-border shadow-sm">
                  <button onClick={() => setOrderDiscountType('amount')} className={cn("px-3 py-1 rounded-lg text-[10px] font-bold transition-all", orderDiscountType === 'amount' ? "bg-primary text-primary-foreground shadow-md" : "opacity-40 hover:opacity-100")}>Amt</button>
                  <button onClick={() => setOrderDiscountType('percent')} className={cn("px-3 py-1 rounded-lg text-[10px] font-bold transition-all", orderDiscountType === 'percent' ? "bg-primary text-primary-foreground shadow-md" : "opacity-40 hover:opacity-100")}>%</button>
                </div>
                <div className="relative w-28">
                  <input 
                    type="number"
                    value={orderDiscount || ''}
                    placeholder="0.00"
                    onChange={(e) => handleOrderDiscountChange(parseFloat(e.target.value) || 0)}
                    className="w-full bg-background border border-border pl-3 pr-8 py-2 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-primary/20 transition-all"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold opacity-40">{orderDiscountType === 'percent' ? '%' : (getDefaultCurrency(orgSettings))}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between text-sm font-medium text-foreground">
              <span>Subtotal</span>
              <span className="font-mono">{getDefaultCurrency(orgSettings)}{(subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            
            {orderDiscount > 0 && (
              <div className="flex justify-between text-sm text-primary font-bold">
                <span>Discount</span>
                <span className="font-mono">-{getDefaultCurrency(orgSettings)}{(calculateOrderDiscount() || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            )}
            
            <div className="flex justify-between text-sm font-medium text-foreground">
              <span>Tax</span>
              <span className="font-mono">{getDefaultCurrency(orgSettings)}{(totalTax || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            
            <div className="flex justify-between items-center pt-6 border-t border-border text-foreground">
              <span className="text-lg font-bold italic serif">Total</span>
              <span className="font-mono text-3xl font-bold tracking-tighter">
                {total < 0 ? '-' : ''}
                {getDefaultCurrency(orgSettings)}
                {Math.abs(total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {checkoutSuccess ? (
            <div className="space-y-4 animate-in fade-in zoom-in pt-3">
              <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-xl flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <div className="text-xs">
                  <p className="font-bold">Sale Successful!</p>
                  <p className="opacity-80">Transaction has been recorded.</p>
                </div>
              </div>
              <button 
                onClick={() => handlePrintReceipt(lastReceipt)}
                className="w-full py-4 bg-[#141414] text-[#E4E3E0] rounded-xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              >
                <Printer className="w-4 h-4" />
                <span>Print Receipt</span>
              </button>
              
              {lastInvoiceId && (
                <div className="pt-6 border-t border-border">
                  <h4 className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-4 flex items-center gap-2">
                    <HistoryIcon className="w-3 h-3" />
                    History & Notes
                  </h4>
                  <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-1">
                    <HistoryNotesView orgId={orgId} entityId={lastInvoiceId} entityType="Invoice" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 pt-2">
              <button 
                disabled={cart.length === 0 || isCheckingOut}
                onClick={() => handleCheckout('Parked')}
                className="flex flex-col items-center justify-center gap-1.5 bg-muted hover:bg-muted/80 p-3 rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed border border-border"
              >
                <Pause className="w-4 h-4 opacity-70" />
                <span className="text-[9px] font-bold uppercase tracking-widest opacity-70">Park</span>
              </button>
               <button 
                disabled={cart.length === 0 || isCheckingOut}
                onClick={() => {
                  setAmountPaid(total - alreadyPaid);
                  setShowCheckoutModal(true);
                }}
                className="col-span-2 flex items-center justify-center gap-3 bg-primary text-primary-foreground hover:opacity-90 p-4 rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-md"
              >
                <ShoppingCart className="w-5 h-5" />
                <span className="text-sm font-bold uppercase tracking-wider">{total < 0 ? 'Refund Total' : 'Pay & Check-out'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Edit Item Modal */}
      {editingItem && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-black/40 backdrop-blur-md animate-in fade-in duration-300"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setEditingItem(null);
          }}
        >
          <div className="bg-background w-full max-w-md rounded-[2.5rem] shadow-2xl border border-border overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-border flex items-center justify-between bg-card/80 backdrop-blur-sm">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <h3 className="text-xl font-bold italic serif tracking-tight">Edit Line Item</h3>
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-40">{editingItem.name}</p>
              </div>
              <button 
                onClick={() => setEditingItem(null)}
                className="w-10 h-10 flex items-center justify-center hover:bg-[#141414]/5 rounded-full transition-all active:scale-90"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-8 space-y-8">
              {/* Summary Preview */}
              <div className="bg-[#141414] text-[#E4E3E0] p-6 rounded-[2rem] shadow-xl flex items-center justify-between overflow-hidden relative group dark:bg-[#000000]">
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/5 rounded-full blur-2xl group-hover:bg-white/10 transition-all duration-500" />
                <div className="relative z-10">
                  <p className="text-[10px] uppercase tracking-widest opacity-50 font-bold mb-1">Line Total</p>
                  <p className="text-3xl font-bold italic serif tracking-tighter">
                    {getDefaultCurrency(orgSettings)}{(calculateItemTotal(editingItem) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="text-right relative z-10">
                  <p className="text-[10px] uppercase tracking-widest opacity-50 font-bold mb-1">Quantity</p>
                  <p className="text-xl font-bold font-mono">x{editingItem.quantity}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-40 flex items-center gap-2">
                    <Package className="w-3 h-3" />
                    Quantity
                  </label>
                  <div className="flex items-center gap-2 bg-white border border-[#141414]/10 rounded-2xl p-1.5 shadow-sm">
                    <button 
                      onClick={() => setEditingItem({ ...editingItem, quantity: Math.max(1, editingItem.quantity - 1) })}
                      className="w-9 h-9 rounded-xl bg-[#141414]/5 flex items-center justify-center hover:bg-[#141414] hover:text-[#E4E3E0] transition-all active:scale-90"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input 
                      type="number"
                      value={editingItem.quantity || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="flex-1 bg-transparent text-center font-bold text-base outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button 
                      onClick={() => setEditingItem({ ...editingItem, quantity: editingItem.quantity + 1 })}
                      className="w-9 h-9 rounded-xl bg-[#141414]/5 flex items-center justify-center hover:bg-[#141414] hover:text-[#E4E3E0] transition-all active:scale-90"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-40 flex items-center gap-2">
                    <Banknote className="w-3 h-3" />
                    Unit Price
                  </label>
                  <div className="relative group">
                    <input 
                      type="number"
                      value={editingItem.unitPrice || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, unitPrice: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-white border border-[#141414]/10 pl-4 pr-10 py-3.5 rounded-2xl text-base font-bold outline-none focus:ring-2 ring-primary/20 transition-all shadow-sm"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold opacity-30 group-focus-within:opacity-100 transition-opacity">{getDefaultCurrency(orgSettings)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-40 flex items-center gap-2">
                    <Tag className="w-3 h-3" />
                    Discount
                  </label>
                  <div className="flex bg-white/50 p-1 rounded-xl border border-[#141414]/10 shadow-inner">
                    <button 
                      onClick={() => setEditingItem({ ...editingItem, discountType: 'amount' })}
                      className={cn(
                        "px-4 py-1.5 rounded-lg text-[10px] font-bold transition-all uppercase tracking-wider",
                        editingItem.discountType === 'amount' ? "bg-[#141414] text-[#E4E3E0] shadow-lg" : "opacity-40 hover:opacity-100"
                      )}
                    >
                      Amount
                    </button>
                    <button 
                      onClick={() => setEditingItem({ ...editingItem, discountType: 'percent' })}
                      className={cn(
                        "px-4 py-1.5 rounded-lg text-[10px] font-bold transition-all uppercase tracking-wider",
                        editingItem.discountType === 'percent' ? "bg-[#141414] text-[#E4E3E0] shadow-lg" : "opacity-40 hover:opacity-100"
                      )}
                    >
                      Percent
                    </button>
                  </div>
                </div>
                <div className="relative group">
                  <input 
                    type="number"
                    value={isNaN(editingItem.discount || 0) ? '' : (editingItem.discount || '')}
                    placeholder="0.00"
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      if (val !== 0 && !validateDiscount(val, editingItem.discountType || 'percent')) {
                        const selectedStore = stores.find(s => s.id === selectedStoreId);
                        const userRule = selectedStore?.discountRules?.find((rule: any) => 
                          rule.userIds.includes(auth.currentUser?.uid)
                        );
                        if (userRule) {
                          notify.error(`Discount must be between ${userRule.minDiscount}% and ${userRule.maxDiscount}%`);
                        } else {
                          notify.error('You do not have permission to apply discounts.');
                        }
                        return;
                      }
                      setEditingItem({ ...editingItem, discount: val });
                    }}
                    className="w-full bg-white border border-[#141414]/10 pl-4 pr-10 py-3.5 rounded-2xl text-base font-bold outline-none focus:ring-2 ring-primary/20 transition-all shadow-sm"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold opacity-30 group-focus-within:opacity-100 transition-opacity">
                    {editingItem.discountType === 'percent' ? '%' : (getDefaultCurrency(orgSettings))}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.15em] opacity-40 flex items-center gap-2">
                  <FileText className="w-3 h-3" />
                  Line Notes
                </label>
                <textarea 
                  value={editingItem.notes || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })}
                  placeholder="Add specific instructions for this item..."
                  className="w-full bg-white border border-[#141414]/10 p-4 rounded-2xl text-sm font-medium outline-none focus:ring-2 ring-primary/20 transition-all shadow-sm min-h-[100px] resize-none"
                />
              </div>

              <div className="pt-4 flex gap-4">
                <button 
                  onClick={() => setEditingItem(null)}
                  className="flex-1 px-6 py-4 rounded-2xl text-xs font-bold border border-[#141414]/10 hover:bg-[#141414]/5 transition-all active:scale-95"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    updateCartItem(editingItem.id, editingItem.warehouseId, editingItem.lotNumber, {
                      quantity: editingItem.quantity,
                      unitPrice: editingItem.unitPrice,
                      discount: editingItem.discount,
                      discountType: editingItem.discountType,
                      notes: editingItem.notes
                    });
                    setEditingItem(null);
                  }}
                  className="flex-1 px-6 py-4 rounded-2xl text-xs font-bold bg-[#141414] text-[#E4E3E0] hover:shadow-2xl hover:-translate-y-0.5 transition-all active:scale-95 shadow-xl"
                >
                  Apply Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Add Item Modal */}
      {selectedItemForAdd && (
        <div
          className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center overflow-y-auto overscroll-contain p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSelectedItemForAdd(null);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-sm rounded-[2rem] p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold italic serif leading-tight">Add Item</h2>
                <p className="text-xs opacity-60 mt-1">{selectedItemForAdd.name}</p>
              </div>
              <button onClick={() => setSelectedItemForAdd(null)} className="p-2 bg-white rounded-full hover:bg-gray-50 transition-colors border border-[#141414]/10 shadow-sm">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCartAddConfirm} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1 block">Quantity</label>
                  <input 
                    type="number" 
                    min="1" 
                    required 
                    value={addItemForm.quantity} 
                    onChange={e => setAddItemForm({...addItemForm, quantity: parseInt(e.target.value) || 1})} 
                    className="w-full bg-white border border-[#141414]/10 p-2.5 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-primary/20" 
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1 block">Warehouse</label>
                  <SearchableSelect 
                    value={addItemForm.warehouseId}
                    onChange={(e) => {
                      const val = e.target.value;
                      const selected = warehouses.find((w: any) => w.id === val);
                      setAddItemForm({...addItemForm, warehouseId: val, warehouseName: selected?.name || ''});
                    }}
                    options={warehouses.map((w: any) => ({
                      id: w.id,
                      label: w.name
                    }))}
                    placeholder="Select Warehouse"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1 block">Lot Number</label>
                  <SearchableSelect 
                    value={addItemForm.lotNumber}
                    onChange={(e) => {
                      const val = e.target.value;
                      const lotNo = val;
                      const selectedLot = lotNumbers.find(l => l.lotNumber === lotNo);
                      setAddItemForm({
                        ...addItemForm, 
                        lotNumber: lotNo,
                        expiryDate: selectedLot?.expiryDate || ''
                      });
                    }}
                    options={[
                      { id: 'AUTO', label: 'Auto (Newest)' },
                      ...lotNumbers
                        .filter(l => !l.productId || l.productId === selectedItemForAdd.id)
                        .map(l => ({
                          id: l.lotNumber,
                          label: l.lotNumber,
                          subLabel: l.expiryDate ? `Exp: ${l.expiryDate}` : undefined
                        }))
                    ]}
                    placeholder="Lot Number"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1 block">Expiry Date</label>
                  <input 
                    type="date" 
                    value={addItemForm.expiryDate || ''} 
                    onChange={e => setAddItemForm({...addItemForm, expiryDate: e.target.value})} 
                    className="w-full bg-white border border-[#141414]/10 p-2.5 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-primary/20" 
                    readOnly={addItemForm.lotNumber !== 'AUTO'}
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1 block">Discount</label>
                <div className="flex gap-2">
                   <div className="flex bg-white p-1 rounded-xl border border-[#141414]/10">
                    <button type="button" onClick={() => setAddItemForm({...addItemForm, discountType: 'amount'})} className={cn("px-3 py-1.5 rounded-lg text-xs font-bold transition-all", addItemForm.discountType === 'amount' ? "bg-gray-100 text-black shadow-sm" : "opacity-40 hover:opacity-80")}>Amt</button>
                    <button type="button" onClick={() => setAddItemForm({...addItemForm, discountType: 'percent'})} className={cn("px-3 py-1.5 rounded-lg text-xs font-bold transition-all", addItemForm.discountType === 'percent' ? "bg-gray-100 text-black shadow-sm" : "opacity-40 hover:opacity-80")}>%</button>
                  </div>
                  <div className="relative flex-1">
                    <input 
                      type="number" 
                      min="0" 
                      step="0.01"
                      value={addItemForm.discount || ''} 
                      onChange={e => setAddItemForm({...addItemForm, discount: parseFloat(e.target.value) || 0})} 
                      className="w-full bg-white border border-[#141414]/10 pl-3 pr-8 py-2.5 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-primary/20" 
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold opacity-40">{addItemForm.discountType === 'percent' ? '%' : (getDefaultCurrency(orgSettings))}</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 flex gap-3 border-t border-[#141414]/10">
                <button type="button" onClick={() => setSelectedItemForAdd(null)} className="flex-1 py-3 rounded-full border border-[#141414]/20 font-bold hover:bg-gray-50 transition-colors bg-white">Cancel</button>
                <button type="submit" className="flex-1 py-3 rounded-full bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity shadow-md">Add to Cart</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-md animate-in fade-in duration-300"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowCheckoutModal(false);
          }}
        >
          <div className="bg-[#E4E3E0] w-full max-w-md rounded-[2.5rem] shadow-2xl border border-[#141414]/10 overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-[#141414]/10 flex items-center justify-between">
              <h2 className="text-2xl font-bold italic serif">{total < 0 ? 'Refund' : 'Check-out'}</h2>
              <button 
                onClick={() => setShowCheckoutModal(false)}
                className="p-2 hover:bg-[#141414]/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Payment Method</label>
                <div className="grid grid-cols-2 gap-3 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                    {(() => {
                      const selectedStore = stores.find(s => s.id === selectedStoreId);
                      const allowedIds = selectedStore?.allowedPaymentAccountIds || [];
                      
                      const filteredBanks = allowedIds.length > 0 
                        ? bankAccounts.filter(acc => allowedIds.includes(acc.id))
                        : bankAccounts;
                        
                      const filteredCOA = allowedIds.length > 0
                        ? chartOfAccounts.filter(acc => allowedIds.includes(acc.id))
                        : chartOfAccounts.filter(acc => acc.enablePayments || acc.type === 'Bank');

                      if (filteredBanks.length === 0 && filteredCOA.length === 0) {
                        return (
                          <div className="col-span-2 p-4 text-center border border-dashed border-[#141414]/20 rounded-2xl">
                            <p className="text-xs opacity-50 italic">No payment methods available for this store.</p>
                          </div>
                        );
                      }

                      return (
                        <>
                          {filteredBanks.map(account => (
                            <button
                              key={account.id}
                              onClick={() => setSelectedBankAccountId(account.id)}
                              className={cn(
                                "flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border transition-all text-center h-24",
                                selectedBankAccountId === account.id 
                                  ? "bg-[#141414] text-[#E4E3E0] border-[#141414] shadow-lg" 
                                  : "bg-white border-[#141414]/10 hover:border-[#141414]/30 hover:bg-white/50"
                              )}
                            >
                              <Building2 className={cn("w-5 h-5", selectedBankAccountId === account.id ? "text-white" : "text-[#141414]/40")} />
                              <div>
                                <p className="text-[10px] font-bold leading-tight truncate w-full">{account.accountName}</p>
                                <p className="text-[8px] opacity-60 mt-0.5 truncate w-full">{account.accountNumber}</p>
                              </div>
                            </button>
                          ))}
                          {filteredCOA.map(account => (
                            <button
                              key={account.id}
                              onClick={() => setSelectedBankAccountId(account.id)}
                              className={cn(
                                "flex flex-col items-center justify-center gap-2 p-3 rounded-2xl border transition-all text-center h-24",
                                selectedBankAccountId === account.id 
                                  ? "bg-[#141414] text-[#E4E3E0] border-[#141414] shadow-lg" 
                                  : "bg-white border-[#141414]/10 hover:border-[#141414]/30 hover:bg-white/50"
                              )}
                            >
                              <Building2 className={cn("w-5 h-5", selectedBankAccountId === account.id ? "text-white" : "text-[#141414]/40")} />
                              <div>
                                <p className="text-[10px] font-bold leading-tight truncate w-full">{account.name}</p>
                                <p className="text-[8px] opacity-60 mt-0.5 truncate w-full">{account.code}</p>
                              </div>
                            </button>
                          ))}
                        </>
                      );
                    })()}
                </div>
                </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">{total < 0 ? 'Refund Amount' : 'Amount Paid'}</label>
                <div className="relative">
                  <input 
                    type="number"
                    value={isNaN(amountPaid) ? '' : amountPaid}
                    onChange={(e) => setAmountPaid(parseFloat(e.target.value) || 0)}
                    className="w-full bg-white border border-[#141414]/10 pl-4 pr-12 py-4 rounded-2xl text-xl font-mono font-bold outline-none focus:ring-2 ring-primary/20 transition-all text-right"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg font-bold opacity-40">{getDefaultCurrency(orgSettings)}</span>
                </div>
                <div className="space-y-1 px-1">
                  <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                    <span className="opacity-40">Total Amount:</span>
                    <span>{getDefaultCurrency(orgSettings)}{(total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  {alreadyPaid > 0 && (
                    <>
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-green-600">
                        <span>Already Paid:</span>
                        <span>{getDefaultCurrency(orgSettings)}{(alreadyPaid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
                        <span className="opacity-40">Remaining Balance:</span>
                        <span className="text-[#141414]">{getDefaultCurrency(orgSettings)}{((total || 0) - (alreadyPaid || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <button
                disabled={!selectedBankAccountId || amountPaid === 0 || isCheckingOut}
                onClick={() => {
                  const selectedAccount = bankAccounts.find(a => a.id === selectedBankAccountId) || 
                                         chartOfAccounts.find(a => a.id === selectedBankAccountId);
                  const accountName = selectedAccount?.accountName || selectedAccount?.name || 'Cash';
                  handleCheckout('Paid', accountName, selectedBankAccountId, amountPaid);
                }}
                className="w-full py-4 bg-primary text-primary-foreground rounded-full font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-20 shadow-xl"
              >
                {isCheckingOut ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>{total < 0 ? 'Confirm Refund' : 'Confirm Payment'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Parked Invoices Modal */}
      {showParkedModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-md animate-in fade-in duration-300"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowParkedModal(false);
          }}
        >
          <div className="bg-[#E4E3E0] w-full max-w-2xl rounded-[2.5rem] shadow-2xl border border-[#141414]/10 overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-8 border-b border-[#141414]/10 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold italic serif">Parked Invoices</h2>
                <p className="text-xs opacity-50 font-bold uppercase tracking-widest mt-1">Parked Invoices from POS</p>
              </div>
              <button 
                onClick={() => setShowParkedModal(false)}
                className="p-2 hover:bg-[#141414]/10 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-8 max-h-[60vh] overflow-y-auto custom-scrollbar">
              {parkedInvoices.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {parkedInvoices.map(invoice => (
                    <div 
                      key={invoice.id}
                      className="bg-white border border-[#141414]/10 p-6 rounded-3xl flex items-center justify-between hover:border-[#141414]/30 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-[#141414]/5 flex items-center justify-center">
                          <Pause className="w-6 h-6 opacity-40" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold">{invoice.number}</span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-600 font-bold uppercase tracking-wider">Parked</span>
                          </div>
                          <p className="text-xs opacity-60 font-medium">{invoice.contactName}</p>
                          <p className="text-[10px] opacity-40 mt-1">{(() => {
                            try {
                              return new Date(invoice.createdAt?.toDate?.() || invoice.createdAt).toLocaleString();
                            } catch (e) {
                              return 'Invalid Date';
                            }
                          })()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Total Amount</p>
                          <p className="text-lg font-mono font-bold">{getDefaultCurrency(orgSettings)}{(invoice.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        </div>
                        <button 
                          onClick={() => resumeParkedInvoice(invoice)}
                          className="bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full font-bold text-xs flex items-center gap-2 hover:opacity-90 transition-all shadow-lg shadow-[#141414]/10"
                        >
                          <span>Resume Sale</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={async () => {
                            try {
                              await deleteDoc(doc(db, 'documents', invoice.id));
                              notify.confirm(`Deleted parked invoice: ${invoice.number}`);
                            } catch (error) {
                              console.error("Error deleting parked invoice:", error);
                              notify.error('Failed to delete parked invoice');
                            }
                          }}
                          className="p-3 text-red-500 hover:bg-red-50 rounded-full transition-all"
                          title="Delete Parked Invoice"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-20 text-center space-y-4">
                  <div className="w-20 h-20 rounded-full bg-[#141414]/5 flex items-center justify-center mx-auto">
                    <Pause className="w-10 h-10 opacity-10" />
                  </div>
                  <div>
                    <p className="text-lg font-bold italic serif">No parked invoices</p>
                    <p className="text-xs opacity-50">Invoices parked during checkout will appear here.</p>
                  </div>
                </div>
              )}
            </div>
            <div className="p-8 bg-[#141414]/5 border-t border-[#141414]/10 flex justify-end">
              <button 
                onClick={() => setShowParkedModal(false)}
                className="px-8 py-3 rounded-full font-bold text-xs hover:bg-[#141414]/5 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
